import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearMessages,
  clearReplyMessage,
  getMessagesPaginated,
  loadMoreMessages,
  stopSwitchingLoading,
} from '@/store/chat/actions';

import { selectChat, selectIsSwitchingChat } from '@/store/chat/selectors';

import { Spin } from 'antd';

import DateComponent from './Date';
import Message from './Message';
import ThinkingMessage from './ThinkingMessage';

import ChatThinkingManager from '@/utils/ChatThinkingManager';

import classNames from 'classnames';
import dayjs from 'dayjs';

import styles from '../Messages.module.scss';

const Main = ({ socket, containerRef }) => {
  const dispatch = useDispatch();
  const { selectedUser, selectedChatTopic, messages, replyMessage, hasMoreMessages, isLoadingMessages, currentPage } =
    useSelector(selectChat);
  const isSwitchingChat = useSelector(selectIsSwitchingChat);
  const prevChatIdRef = useRef(null);
  const [isScrollLoading, setIsScrollLoading] = useState(false); // Additional loading state for throttle
  const lastLoadTimeRef = useRef(0); // For throttling requests
  const scrollRestoreRef = useRef(null); // For storing scroll restoration data
  const { t } = useTranslation('chat');

  // Function to smoothly restore scroll position after loading new messages
  const restoreScrollPosition = useCallback(
    (previousScrollHeight, targetScrollPosition) => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      let attempts = 0;
      const maxAttempts = 10;
      const attemptDelay = 16; // ~60fps

      const checkAndRestore = () => {
        const currentScrollHeight = container.scrollHeight;
        attempts++;

        console.log(`📏 Restore attempt ${attempts}:`, {
          previousHeight: previousScrollHeight,
          currentHeight: currentScrollHeight,
          targetPosition: targetScrollPosition,
          heightDiff: currentScrollHeight - previousScrollHeight,
        });

        // Check if new content has been rendered (height increased significantly)
        if (currentScrollHeight > previousScrollHeight + 50) {
          // For column-reverse with messages appended to END:
          // New content is added to the bottom, so we don't need to adjust scroll position
          // We should stay at the same visual position

          console.log('📏 Keeping scroll position for column-reverse with appended messages');

          // Keep the same scroll position since new content was added to bottom
          container.scrollTop = targetScrollPosition;

          setIsScrollLoading(false);
          scrollRestoreRef.current = null;
          return true;
        }

        // Continue trying if we haven't reached max attempts
        if (attempts < maxAttempts) {
          setTimeout(checkAndRestore, attemptDelay);
        } else {
          // Fallback: keep original position
          console.warn('📏 Scroll restore timeout, keeping original position');
          container.scrollTop = targetScrollPosition;
          setIsScrollLoading(false);
          scrollRestoreRef.current = null;
        }

        return false;
      };

      // Start the restore process with a small delay to allow DOM update
      setTimeout(checkAndRestore, attemptDelay);
    },
    [containerRef]
  );

  useEffect(() => {
    const currentChatId = selectedChatTopic?.chatId || selectedUser?.chatId;
    console.log('🔄 Main useEffect triggered. Current chat ID:', currentChatId, 'Previous:', prevChatIdRef.current);

    // для новых чатов
    if (!currentChatId) {
      dispatch(stopSwitchingLoading());
      return;
    }

    // Загружаем сообщения только при реальной смене чата и если chatId валидный
    if (currentChatId && currentChatId !== prevChatIdRef.current) {
      console.log('📥 Chat changed, loading initial messages for chat:', currentChatId);

      // Обновляем ссылку на предыдущий чат
      prevChatIdRef.current = currentChatId;

      // Сначала полностью очищаем все сообщения от предыдущего чата
      dispatch(clearMessages());
      dispatch(clearReplyMessage());

      // Затем загружаем первые 20 сообщений с пагинацией
      dispatch(getMessagesPaginated(currentChatId, 0, 20));
    } else if (currentChatId === prevChatIdRef.current) {
      console.log('🔄 Same chat, skipping message reload');
    }
  }, [selectedUser?.chatId, selectedChatTopic?.chatId, dispatch]); // Убираем selectedUser и selectedChatTopic из зависимостей

  // Синхронизация ChatThinkingManager с Redux store
  useEffect(() => {
    // Синхронизируем состояние менеджера с Redux
    ChatThinkingManager.syncWithRedux(messages);
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      const chatId = selectedChatTopic?.chatId || selectedUser?.chatId;
      if (chatId) {
        socket?.emit('readMessages', { chatId });
      }
    }
    return () => socket?.off('readMessages');
  }, [messages.length, selectedUser?.chatId, selectedChatTopic?.chatId, socket]); // Оптимизируем зависимости

  // Handle scroll for pagination with throttle (2 seconds)
  const handleScroll = useCallback(() => {
    if (!containerRef.current || isLoadingMessages || isScrollLoading) return;

    const container = containerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const now = Date.now();

    // Throttle: prevent requests more often than every 2 seconds
    if (now - lastLoadTimeRef.current < 2000) {
      return;
    }

    // For column-reverse: when scrolled near the bottom (old messages), scrollTop is high
    // Check if scrolled within 400px from the bottom (where old messages are)
    const distanceFromBottom = scrollHeight + scrollTop - clientHeight;
    console.log('📏 Scroll metrics:', { scrollTop, scrollHeight, clientHeight, distanceFromBottom });

    if (distanceFromBottom < 400 && hasMoreMessages) {
      const currentChatId = selectedChatTopic?.chatId || selectedUser?.chatId;
      if (currentChatId) {
        const nextPage = (currentPage || 0) + 1;
        console.log('📄 Loading more messages, next page:', nextPage);

        // Set loading state and update timestamp
        setIsScrollLoading(true);
        lastLoadTimeRef.current = now;

        // Save current scroll position and height for restoration
        const previousScrollHeight = scrollHeight;
        const savedScrollPosition = scrollTop;

        // Store restoration data
        scrollRestoreRef.current = {
          previousScrollHeight,
          savedScrollPosition,
          attempts: 0,
        };

        console.log('📄 Starting load with restore data:', scrollRestoreRef.current);

        dispatch(loadMoreMessages(currentChatId, nextPage, 20));
      }
    }
  }, [
    containerRef,
    isLoadingMessages,
    isScrollLoading,
    hasMoreMessages,
    selectedChatTopic,
    selectedUser,
    currentPage,
    dispatch,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Reset loading state when messages change
  useEffect(() => {
    if (isScrollLoading) {
      const timer = setTimeout(() => {
        setIsScrollLoading(false);
      }, 500); // Safety timeout
      return () => clearTimeout(timer);
    }
  }, [messages.length]);

  // Handle scroll position restoration when new messages are loaded
  useEffect(() => {
    if (scrollRestoreRef.current && messages.length > 0 && !isLoadingMessages) {
      const { previousScrollHeight, savedScrollPosition } = scrollRestoreRef.current;

      console.log('📏 Triggering scroll restore effect:', {
        hasRestoreData: !!scrollRestoreRef.current,
        messagesCount: messages.length,
        isLoading: isLoadingMessages,
      });

      // Use immediate timeout to ensure DOM has updated
      restoreScrollPosition(previousScrollHeight, savedScrollPosition);
    }
  }, [messages.length, isLoadingMessages, restoreScrollPosition]);

  const checkFormat = (messageDate, currentDate) => {
    if (dayjs(messageDate).year() !== dayjs(currentDate).year()) {
      return 'DD MMMM YYYY';
    }

    return 'DD MMMM';
  };

  const renderMessages = () => {
    console.log('🎨 Rendering messages, total count:', messages?.length);
    // console.log('📋 Messages data:', messages);

    return messages?.map((message, index) => {
      const messageId = message.messageData?.messageId;
      const currentMessageDate = message?.messageData?.createdAt;
      const currentDate = new Date();

      // Check if it's a thinking message
      const isThinking = message?.messageData?.isThinking;
      const MessageComponent = isThinking ? ThinkingMessage : Message;

      // console.log(`📝 Message ${index}:`, {
      //   messageId: message.messageData.messageId,
      //   isThinking,
      //   content: message.messageData.content,
      //   component: isThinking ? 'ThinkingMessage' : 'Message',
      // });

      if (index + 1 === messages.length) {
        return (
          <React.Fragment key={messageId}>
            <MessageComponent containerRef={containerRef} data={message} />
            <DateComponent text={dayjs(currentMessageDate).format(checkFormat(currentMessageDate, currentDate))} />
          </React.Fragment>
        );
      }

      if (
        dayjs(currentMessageDate).format('DD:MM:YYYY') ===
        dayjs(messages[index + 1].messageData.createdAt).format('DD:MM:YYYY')
      ) {
        return <MessageComponent containerRef={containerRef} data={message} key={messageId} />;
      }

      return (
        <React.Fragment key={messageId}>
          <MessageComponent containerRef={containerRef} data={message} />
          <DateComponent text={dayjs(currentMessageDate).format(checkFormat(currentMessageDate, currentDate))} />
        </React.Fragment>
      );
    });
  };

  return (
    <div ref={containerRef} className={replyMessage ? classNames(styles.main, styles.main_reply) : styles.main}>
      {/* Индикатор переключения чата */}
      {isSwitchingChat && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            padding: '16px 24px',
            color: '#1890ff',
            fontSize: '14px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '16px',
            border: '1px solid rgba(24, 144, 255, 0.2)',
            zIndex: 100,
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            fontWeight: 500,
          }}
        >
          <Spin spinning></Spin>
        </div>
      )}

      {!isSwitchingChat && renderMessages()}

      {/* Индикатор загрузки дополнительных сообщений */}
      {!isSwitchingChat && (isLoadingMessages || isScrollLoading) && hasMoreMessages && (
        <div
          style={{
            position: 'sticky',
            top: '10px',
            textAlign: 'center',
            padding: '8px 12px',
            color: '#1890ff',
            fontSize: '12px',
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            borderRadius: '12px',
            margin: '8px auto 0',
            border: '1px solid rgba(24, 144, 255, 0.2)',
            width: 'fit-content',
            zIndex: 10,
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.1)',
          }}
        >
          {t('loadingMessages')}
        </div>
      )}
    </div>
  );
};

export default Main;
